// Client-side name matching for discovery search.
//
// The provider already searched: DoorDash matches by category, plurals,
// punctuation and spelling ("tacos" finds Taco Bell, "pizza" finds Domino's),
// and Phantom's platform path ranks with its own match ladder. Rows the
// provider returned for the query on screen are therefore never re-filtered
// here (prod QA 2026-10-06: a client name filter hid McDonald's for
// "mcdonalds" and Chick-fil-A for "chick fil a"). This matcher is only for rows
// that did NOT come back for the current query: the team's injected go-to,
// saved and visited restaurants, and the previous query's rows while the new
// search is in flight.

// The search identity a row set answers: what the source sends as `query`.
export const searchQueryKey = (value) => String(value || '').trim().toLowerCase();

// Lowercase, diacritics stripped, apostrophes deleted (McDonald's ==
// McDonalds), "&" == "and", hyphens and other punctuation as spaces
// (chick-fil-a == chick fil a). Same rules as Phantom's normalizeSearchText.
export const normalizeSearchText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Simple plural folding for a query word: tacos -> taco, burgers -> burger,
// sandwiches -> sandwich. The stem is a prefix of the word, so it still
// matches a plural name ("taco" is in "tacos").
const stemWord = (word) => {
  if (word.length >= 5 && /(?:s|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2);
  if (word.length >= 4 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
};

/**
 * True when `row` plausibly answers `query`. Name and cuisine match word by
 * word in any order: at least half the words must appear (one mistyped word
 * doesn't sink a good match), and one-letter words ("a" in "chick fil a")
 * don't count. The whole query also matches with spacing ignored, so
 * "chickfila" finds Chick-fil-A. Description and address only match the
 * whole query: single words there are too noisy.
 */
export const rowMatchesQuery = (row, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  const haystack = normalizeSearchText([row?.name, row?.cuisine].filter(Boolean).join(' '));
  if (haystack) {
    if (haystack.replace(/ /g, '').includes(normalizedQuery.replace(/ /g, ''))) return true;
    const words = normalizedQuery.split(' ');
    const meaningful = words.filter((word) => word.length > 1);
    const counted = meaningful.length ? meaningful : words;
    const matched = counted.filter((word) => haystack.includes(stemWord(word))).length;
    if (matched >= counted.length / 2) return true;
  }
  return [row?.description, row?.address || row?._address].some((field) =>
    normalizeSearchText(field).includes(normalizedQuery)
  );
};

export const filterRowsByQuery = (rows, query) => {
  if (!normalizeSearchText(query)) return rows;
  return rows.filter((row) => rowMatchesQuery(row, query));
};

/** True when the row's NAME matches the query. */
export const nameMatchesQuery = (row, query) => rowMatchesQuery({ name: row?.name }, query);

/**
 * How closely the row's NAME answers the query: the search ranking under
 * Recommended. A yes/no boost from the loose matcher above tied Chick-fil-A
 * with every "...Chicken" store for "chick-fil-a" (half the words match),
 * so a closer or better-rated chicken place beat it (2026-10-07).
 *   3: the name holds the whole query, spacing ignored ("Chick-fil-A" for
 *      "chick fil a", "McDonald's" for "mcdonalds")
 *   2: every word of the query starts a word of the name ("Chipotle Mexican
 *      Grill" for "chipotle grill", "Taco Bell" for "tacos")
 *   1: only the loose match ("Starbird Chicken" for "chick fil a")
 *   0: no match
 */
export const nameMatchRank = (row, query) => {
  const normalizedQuery = normalizeSearchText(query);
  const name = normalizeSearchText(row?.name);
  if (!normalizedQuery || !name) return 0;
  if (name.replace(/ /g, '').includes(normalizedQuery.replace(/ /g, ''))) return 3;
  const words = normalizedQuery.split(' ');
  const meaningful = words.filter((word) => word.length > 1);
  const counted = meaningful.length ? meaningful : words;
  const nameWords = name.split(' ');
  if (counted.every((word) => nameWords.some((nameWord) => nameWord.startsWith(stemWord(word))))) return 2;
  return nameMatchesQuery(row, query) ? 1 : 0;
};
